import axios from "axios";
import { SHA256, enc } from "crypto-js";

const API_BASE_URL = process.env.REACT_APP_API_ROUTE;

/** HTTP statuses that indicate the route is not deployed yet (dashboard KYC list, etc.). */
export const ENDPOINT_NOT_AVAILABLE_STATUSES = [404, 501];

/**
 * @param {import('axios').AxiosError|Error} error
 * @returns {number|undefined}
 */
export const getApiErrorStatus = (error) =>
  error?.response?.status ?? error?.status;

/**
 * @param {import('axios').AxiosError|Error} error
 * @returns {string}
 */
export const getApiErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (typeof error?.response?.data?.message === "string") {
    return error.response.data.message;
  }
  return error?.message || "Unexpected API error";
};

/**
 * @param {import('axios').AxiosError|Error} error
 * @returns {boolean}
 */
export const isEndpointNotAvailable = (error) => {
  const status = getApiErrorStatus(error);
  return ENDPOINT_NOT_AVAILABLE_STATUSES.includes(status);
};

/**
 * Makes an API call to get endpoint details.
 *
 * @param {string} endpoint - The URL endpoint (example: "/terminal-details").
 * @param {Object} data - The body of the request.
 * @param {Object} [headers] - Additional headers for the request.
 * @param {string} token - Token to generate request hash.
 * @param {boolean} [has] - If false, automatically adds hash values to the request body.
 * @returns {Promise<Object>} - The data returned by the API.
 */
export const fetchData = async ({
  endpoint,
  data,
  headers = {},
  token,
  has = null,
}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const dataString = JSON.stringify(data);
  const hash = SHA256(dataString + token).toString(enc.Base64);
  const userHash = SHA256(dataString).toString(enc.Base64);

  if (!has) {
    data.reqhashMAC = hash;
    data.userToken = userHash;
  }

  try {
    // console.log("Request payload →\n", JSON.stringify(data, null, 2));
    const response = await axios.post(url, data, { headers });
    return response;
  } catch (error) {
    console.error(`Error fetching data from ${endpoint}:`, error);
    throw error;
  }
};

export const get = async (url, config = {}) => {
  try {
    const response = await axios.get(`${API_BASE_URL}${url}`, config);
    return response.data;
  } catch (error) {
    console.error(`Error fetching data from ${url}:`, error);
    throw error;
  }
};

export const post = async (url, data, config = {}) => {
  try {
    const response = await axios.post(`${API_BASE_URL}${url}`, data, config);
    return response.data;
  } catch (error) {
    console.error(`Error sending data to ${url}:`, error);
    throw error;
  }
};

export const put = async (url, data, config = {}) => {
  try {
    const response = await axios.put(`${API_BASE_URL}${url}`, data, config);
    return response.data;
  } catch (error) {
    console.error(`Error sending data to ${url}:`, error);
    throw error;
  }
};

export const del = async (url, config = {}) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}${url}`, config);
    return response.data;
  } catch (error) {
    console.error(`Error sending data to ${url}:`, error);
    throw error;
  }
};

export const patch = async (url, data, config = {}) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}${url}`, data, config);
    return response.data;
  } catch (error) {
    console.error(`Error PATCH en ${url}:`, error);
    throw error;
  }
};
